import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { backendURL } from "../../config/constants";
import { useAuth } from "../../providers/AuthContext";
import AuthPopup from "../../components/AuthPopUp";
import CodeBlock from "../Documents/AvatarEmbed/CodeBlock";
import "./AvatarStudio.css";

const DOMAINS = ["beauty", "therapy", "consultation", "medical"];

const LAYOUTS = [
  { id: "inline", name: "Classic", hint: "Video fills the page, controls at the bottom." },
  { id: "widget", name: "Card", hint: "A rounded card you can place in a corner." },
  { id: "chat", name: "Chat", hint: "Video on top, conversation below." },
  { id: "split", name: "Split", hint: "Video on the left, conversation on the right." },
  { id: "minimal", name: "Minimal", hint: "Just the video and a slim action bar." },
];

const POSITIONS = ["top-left", "top-right", "bottom-left", "bottom-right", "center"];
const ASPECTS = ["16:9", "4:3", "1:1", "3:4", "9:16"];
const LANGUAGES = { en: "English", it: "Italiano", fr: "Francais", es: "Espanol", de: "Deutsch", pt: "Portugues" };
const FONTS = { system: "Modern (system)", serif: "Classic serif", rounded: "Friendly rounded", mono: "Technical mono" };

const COLORS = [
  { key: "primary", label: "Buttons and accents", auto: false },
  { key: "button_text", label: "Button text", auto: true },
  { key: "input_background", label: "Input field", auto: true },
  { key: "input_text", label: "Input text", auto: true },
  { key: "input_border", label: "Input border", auto: true },
  { key: "background", label: "Page background", auto: true },
  { key: "surface", label: "Panels", auto: true },
  { key: "text", label: "Page text", auto: true },
  { key: "bubble_avatar", label: "Avatar message bubble", auto: true },
  { key: "bubble_avatar_text", label: "Avatar message text", auto: true },
];

const CONTROLS = [
  { key: "text_input", label: "Text input" },
  { key: "voice_input", label: "Voice input" },
  { key: "face_analysis", label: "Face analysis" },
  { key: "captions", label: "Captions" },
  { key: "mute", label: "Mute button" },
  { key: "fullscreen", label: "Fullscreen button" },
  { key: "stop", label: "End button" },
  { key: "start", label: "Start button" },
];

const CLEAR_COLORS = { button_text: "", input_background: "", input_text: "", input_border: "", background: "", surface: "", text: "", bubble_avatar: "", bubble_avatar_text: "" };
const PRESETS = [
  { name: "Ocean", theme: { ...CLEAR_COLORS, mode: "dark", primary: "#2563eb" } },
  { name: "Teal", theme: { ...CLEAR_COLORS, mode: "dark", primary: "#05b1bd", button_text: "#ffffff" } },
  { name: "Rose", theme: { ...CLEAR_COLORS, mode: "dark", primary: "#e11d48", button_text: "#ffffff" } },
  { name: "Emerald", theme: { ...CLEAR_COLORS, mode: "dark", primary: "#059669", button_text: "#ffffff" } },
  { name: "Violet", theme: { ...CLEAR_COLORS, mode: "dark", primary: "#7c3aed", button_text: "#ffffff" } },
  { name: "Amber", theme: { ...CLEAR_COLORS, mode: "dark", primary: "#f59e0b", button_text: "#111827" } },
  { name: "Clean light", theme: { mode: "light", primary: "#2563eb", button_text: "#ffffff", input_background: "#ffffff", input_text: "#111827", input_border: "#d1d5db", background: "#f3f4f6", surface: "#ffffff", text: "#111827", bubble_avatar: "#e5e7eb", bubble_avatar_text: "#111827" } },
  { name: "Midnight", theme: { mode: "dark", primary: "#38bdf8", button_text: "#0b1220", input_background: "#0f172a", input_text: "#e2e8f0", input_border: "#1e293b", background: "#020617", surface: "#0b1220", text: "#e2e8f0", bubble_avatar: "#1e293b", bubble_avatar_text: "#e2e8f0" } },
];

const DEVICES = [
  { id: "desktop", label: "Desktop" },
  { id: "tablet", label: "Tablet" },
  { id: "phone", label: "Phone" },
];

const parseOrigins = (text) =>
  text
    .split(/[\s,]+/)
    .map((o) => o.trim())
    .filter(Boolean);

const Segment = ({ value, options, onChange, label }) => (
  <div className="studio-segment" role="group" aria-label={label}>
    {options.map(([id, text]) => (
      <button key={id} type="button" className={value === id ? "active" : ""} onClick={() => onChange(id)}>
        {text}
      </button>
    ))}
  </div>
);

const Section = ({ id, title, open, onToggle, children }) => (
  <section className={`studio-section ${open ? "open" : ""}`}>
    <button type="button" className="studio-section-head" aria-expanded={open} onClick={() => onToggle(id)}>
      <span>{title}</span>
      <i aria-hidden="true">{open ? "−" : "+"}</i>
    </button>
    {open && <div className="studio-section-body">{children}</div>}
  </section>
);

const AvatarStudio = () => {
  const { getAccessToken, getRefreshToken, updateTokens, logout, isAuthenticated } = useAuth();
  const location = useLocation();

  const [config, setConfig] = useState(null);
  const [saved, setSaved] = useState(null);
  const [returnUrl, setReturnUrl] = useState("");
  const [originsText, setOriginsText] = useState("");
  const [savedExtras, setSavedExtras] = useState("");
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewView, setPreviewView] = useState("start");
  const [device, setDevice] = useState("desktop");
  const [openSections, setOpenSections] = useState(["layout", "colors"]);
  const [avatars, setAvatars] = useState([]);
  const [avatarId, setAvatarId] = useState("");
  const [domain, setDomain] = useState("beauty");
  const [testCreated, setTestCreated] = useState(false);

  const refreshing = useRef(null);
  const frameRef = useRef(null);
  const frameReady = useRef(false);
  const latest = useRef({});

  const renewToken = useCallback(() => {
    if (!refreshing.current) {
      refreshing.current = (async () => {
        const refresh = getRefreshToken();
        if (!refresh) return false;
        try {
          const res = await fetch(`${backendURL}/users/refresh/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh }),
          });
          if (!res.ok) return false;
          const data = await res.json();
          if (!data.access) return false;
          updateTokens(data.access, data.refresh);
          return true;
        } catch {
          return false;
        } finally {
          setTimeout(() => {
            refreshing.current = null;
          }, 2000);
        }
      })();
    }
    return refreshing.current;
  }, [getRefreshToken, updateTokens]);

  const call = useCallback(
    async (path, options = {}) => {
      const send = () =>
        fetch(`${backendURL}${path}`, {
          ...options,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAccessToken()}` },
        });
      if (!getAccessToken()) {
        setShowAuth(true);
        throw new Error("Please sign in first.");
      }
      let res = await send();
      if (res.status === 401 && (await renewToken())) {
        res = await send();
      }
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (res.status === 401) {
        logout();
        setShowAuth(true);
        throw new Error("Your session expired. Please sign in again.");
      }
      if (!res.ok) {
        throw new Error(data?.error?.message || data?.detail || "Something went wrong. Please try again.");
      }
      return data;
    },
    [getAccessToken, renewToken, logout],
  );

  const applyLoaded = useCallback((data) => {
    setConfig(data.settings.config);
    setSaved(data.settings.config);
    setReturnUrl(data.settings.return_url || "");
    setOriginsText((data.settings.embed_origins || []).join("\n"));
    setSavedExtras(JSON.stringify([data.settings.return_url || "", data.settings.embed_origins || []]));
    setIsDefault(data.is_default);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [settings, list] = await Promise.all([call("/avatar/embed/settings"), call("/avatar/list")]);
      applyLoaded(settings);
      const items = list.avatars || [];
      setAvatars(items);
      if (items.length) setAvatarId((current) => current || items[0].id);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }, [call, applyLoaded]);

  useEffect(() => {
    if (!isAuthenticated || !getAccessToken()) {
      setLoading(false);
      return;
    }
    setPreviewUrl("");
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!message || message.type === "error") return undefined;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  // ----- live preview -----
  const previewOrigin = useMemo(() => {
    try {
      return previewUrl ? new URL(previewUrl).origin : "";
    } catch {
      return "";
    }
  }, [previewUrl]);

  latest.current = { config, previewOrigin, previewView };

  const pushPreview = useCallback(() => {
    const { config: cfg, previewOrigin: origin, previewView: view } = latest.current;
    const frame = frameRef.current && frameRef.current.contentWindow;
    if (!frame || !origin || !cfg || !frameReady.current) return;
    frame.postMessage({ type: "todoai:preview-config", config: cfg }, origin);
    frame.postMessage({ type: "todoai:preview-view", view }, origin);
  }, []);

  const createPreview = useCallback(async () => {
    try {
      frameReady.current = false;
      const data = await call("/avatar/embed/preview", {
        method: "POST",
        body: JSON.stringify({ config: latest.current.config || undefined }),
      });
      setPreviewUrl(data.url);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  }, [call]);

  useEffect(() => {
    if (config && !previewUrl) createPreview();
  }, [config, previewUrl, createPreview]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== previewOrigin || !event.data || event.data.source !== "todoai") return;
      if (event.data.type === "todoai:ready") {
        frameReady.current = true;
        pushPreview();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [previewOrigin, pushPreview]);

  useEffect(() => {
    const timer = setTimeout(pushPreview, 120);
    return () => clearTimeout(timer);
  }, [config, previewView, pushPreview]);

  // ----- editing helpers -----
  const set = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const setIn = (section, key, value) => setConfig((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));
  const toggleSection = (id) => setOpenSections((list) => (list.includes(id) ? list.filter((s) => s !== id) : [...list, id]));

  const extrasNow = JSON.stringify([returnUrl.trim(), parseOrigins(originsText)]);
  const dirty = config && saved && (JSON.stringify(config) !== JSON.stringify(saved) || extrasNow !== savedExtras);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = async () => {
    try {
      setBusy(true);
      setMessage(null);
      const data = await call("/avatar/embed/settings", {
        method: "PUT",
        body: JSON.stringify({ config, return_url: returnUrl.trim(), embed_origins: parseOrigins(originsText) }),
      });
      applyLoaded(data);
      setMessage({ type: "success", text: "Saved. Every new avatar session now uses this look." });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset your avatar page to the default settings?")) return;
    try {
      setBusy(true);
      setMessage(null);
      const data = await call("/avatar/embed/settings", { method: "DELETE" });
      applyLoaded(data);
      setMessage({ type: "success", text: "Settings reset to the defaults." });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const createTestSession = async () => {
    try {
      setBusy(true);
      setMessage(null);
      const data = await call("/avatar/embed/session", {
        method: "POST",
        body: JSON.stringify({ avatar_id: avatarId, domain }),
      });
      setTestCreated(true);
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const backendSnippet = `curl -X POST ${backendURL}/avatar/embed/session \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -d '{ "avatar_id": "${avatarId || "AVATAR_ID"}", "domain": "${domain}" }'`;

  const homeLink = (
    <Link to="/docs/avatar/hosted" className="studio-back">
      ← Documentation
    </Link>
  );

  if (!getAccessToken()) {
    return (
      <div className="studio-app studio-signin">
        <div className="studio-signin-card">
          {homeLink}
          <h1>Avatar Studio</h1>
          <p>Sign in to design how your avatar page looks. Your settings are saved to your account.</p>
          <button className="studio-btn studio-btn-primary" onClick={() => setShowAuth(true)}>
            Sign in
          </button>
        </div>
        {showAuth && (
          <AuthPopup onClose={() => setShowAuth(false)} redirectPath={location.pathname} />
        )}
      </div>
    );
  }

  const status = !config ? "" : dirty ? "Unsaved changes" : isDefault ? "Default settings" : "All changes saved";

  return (
    <div className="studio-app">
      <header className="studio-top">
        <div className="studio-top-left">
          {homeLink}
          <h1>Avatar Studio</h1>
        </div>
        <div className="studio-top-center">
          <div className="studio-segment" role="group" aria-label="Preview size">
            {DEVICES.map((d) => (
              <button key={d.id} type="button" className={device === d.id ? "active" : ""} onClick={() => setDevice(d.id)}>
                {d.label}
              </button>
            ))}
          </div>
          <div className="studio-segment" role="group" aria-label="Preview screen">
            <button type="button" className={previewView === "start" ? "active" : ""} onClick={() => setPreviewView("start")}>Start screen</button>
            <button type="button" className={previewView === "live" ? "active" : ""} onClick={() => setPreviewView("live")}>Conversation</button>
          </div>
        </div>
        <div className="studio-top-right">
          <span className={`studio-state ${dirty ? "dirty" : ""}`}>{status}</span>
          <button type="button" className="studio-btn" disabled={busy || isDefault || !config} onClick={reset}>Reset</button>
          <button type="button" className="studio-btn studio-btn-primary" disabled={busy || !dirty} onClick={save}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </header>

      {message && <div className={`studio-toast studio-toast-${message.type}`} role="status">{message.text}</div>}

      <div className="studio-body">
        <main className="studio-stage">
          <div className={`studio-device studio-device-${device}`}>
            {previewUrl ? (
              <iframe ref={frameRef} title="Avatar page preview" src={previewUrl} allow="autoplay" />
            ) : (
              <div className="studio-loading">{loading ? "Loading your settings..." : "Preparing the preview..."}</div>
            )}
          </div>
          <p className="studio-stage-note">Live preview. Changes appear instantly and no avatar session is started.</p>
        </main>

        <aside className="studio-panel" aria-label="Settings">
          {!config ? (
            <div className="studio-loading">{loading ? "Loading..." : "Settings could not be loaded."}</div>
          ) : (
            <>
              <Section id="layout" title="Layout" open={openSections.includes("layout")} onToggle={toggleSection}>
                <div className="studio-layouts">
                  {LAYOUTS.map((l) => (
                    <button key={l.id} type="button" className={`studio-layout ${config.layout === l.id ? "active" : ""}`} onClick={() => set("layout", l.id)}>
                      <span className={`wire wire-${l.id}`} aria-hidden="true"><i /><b /></span>
                      <strong>{l.name}</strong>
                      <small>{l.hint}</small>
                    </button>
                  ))}
                </div>
                {config.layout === "widget" && (
                  <div className="studio-row">
                    <label>Position
                      <select value={config.position} onChange={(e) => set("position", e.target.value)}>
                        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </label>
                    <label>Card shape
                      <select value={config.aspect_ratio} onChange={(e) => set("aspect_ratio", e.target.value)}>
                        {ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </label>
                  </div>
                )}
                <div className="studio-field">Video fit
                  <Segment label="Video fit" value={config.fit} onChange={(v) => set("fit", v)} options={[["contain", "Whole avatar"], ["cover", "Fill the area"]]} />
                </div>
              </Section>

              <Section id="colors" title="Colors" open={openSections.includes("colors")} onToggle={toggleSection}>
                <div className="studio-presets">
                  {PRESETS.map((p) => (
                    <button key={p.name} type="button" className="studio-preset" onClick={() => setConfig((c) => ({ ...c, theme: { ...c.theme, ...p.theme } }))}>
                      <span style={{ background: p.theme.primary }} />{p.name}
                    </button>
                  ))}
                </div>
                <div className="studio-field">Mode
                  <Segment label="Mode" value={config.theme.mode} onChange={(v) => setIn("theme", "mode", v)} options={[["dark", "Dark"], ["light", "Light"]]} />
                </div>
                <div className="studio-colors">
                  {COLORS.map((c) => {
                    const value = config.theme[c.key] || "";
                    return (
                      <div key={c.key} className="studio-color">
                        <span>{c.label}</span>
                        <div>
                          <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#808080"} onChange={(e) => setIn("theme", c.key, e.target.value)} aria-label={c.label} />
                          <code>{value || "auto"}</code>
                          {c.auto && value && <button type="button" className="studio-link" onClick={() => setIn("theme", c.key, "")}>Auto</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section id="style" title="Style" open={openSections.includes("style")} onToggle={toggleSection}>
                <label>Font
                  <select value={config.theme.font} onChange={(e) => setIn("theme", "font", e.target.value)}>
                    {Object.entries(FONTS).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                  </select>
                </label>
                <div className="studio-field">Button style
                  <Segment label="Button style" value={config.theme.button_style} onChange={(v) => setIn("theme", "button_style", v)} options={[["filled", "Filled"], ["outline", "Outline"], ["soft", "Soft"]]} />
                </div>
                <div className="studio-field">Button shape
                  <Segment label="Button shape" value={config.theme.button_shape} onChange={(v) => setIn("theme", "button_shape", v)} options={[["pill", "Pill"], ["rounded", "Rounded"], ["square", "Square"]]} />
                </div>
                <label>Corner radius: {config.theme.radius}px
                  <input type="range" min="0" max="48" value={config.theme.radius} onChange={(e) => setIn("theme", "radius", Number(e.target.value))} />
                </label>
              </Section>

              <Section id="experience" title="What users can do" open={openSections.includes("experience")} onToggle={toggleSection}>
                <div className="studio-toggles">
                  {CONTROLS.map((c) => (
                    <label key={c.key} className="studio-toggle">
                      <input type="checkbox" checked={!!config.controls[c.key]} onChange={(e) => setIn("controls", c.key, e.target.checked)} />
                      {c.label}
                    </label>
                  ))}
                  <label className="studio-toggle">
                    <input type="checkbox" checked={!!config.auto_start} onChange={(e) => set("auto_start", e.target.checked)} />
                    Start automatically
                  </label>
                </div>
                <p className="studio-hint">Typing, voice and face analysis are handled by the page itself. Nothing to build on your side.</p>
                <label>Spoken greeting
                  <textarea rows={2} maxLength={300} placeholder="What the avatar says when the session starts" value={config.branding.greeting} onChange={(e) => setIn("branding", "greeting", e.target.value)} />
                </label>
              </Section>

              <Section id="text" title="Text and branding" open={openSections.includes("text")} onToggle={toggleSection}>
                <label>Name
                  <input type="text" maxLength={60} value={config.branding.name} onChange={(e) => setIn("branding", "name", e.target.value)} />
                </label>
                <label>Welcome message
                  <textarea rows={2} maxLength={200} value={config.branding.welcome_message} onChange={(e) => setIn("branding", "welcome_message", e.target.value)} />
                </label>
                <label>Start button text
                  <input type="text" maxLength={24} placeholder="Start" value={config.branding.start_label} onChange={(e) => setIn("branding", "start_label", e.target.value)} />
                </label>
                <label>Input placeholder
                  <input type="text" maxLength={60} placeholder="Type a message" value={config.branding.input_placeholder} onChange={(e) => setIn("branding", "input_placeholder", e.target.value)} />
                </label>
                <label>Message when the session ends
                  <input type="text" maxLength={120} placeholder="The session has ended." value={config.branding.end_message} onChange={(e) => setIn("branding", "end_message", e.target.value)} />
                </label>
                <label>Logo URL (https)
                  <input type="url" placeholder="https://" value={config.branding.logo_url} onChange={(e) => setIn("branding", "logo_url", e.target.value)} />
                </label>
                <label>Background image URL (https)
                  <input type="url" placeholder="https://" value={config.branding.background_url} onChange={(e) => setIn("branding", "background_url", e.target.value)} />
                </label>
                <label>Browser tab title
                  <input type="text" maxLength={60} value={config.branding.title} onChange={(e) => setIn("branding", "title", e.target.value)} />
                </label>
                <label>Interface language
                  <select value={config.ui_language} onChange={(e) => set("ui_language", e.target.value)}>
                    {Object.entries(LANGUAGES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                  </select>
                </label>
              </Section>

              <Section id="after" title="After the session" open={openSections.includes("after")} onToggle={toggleSection}>
                <label>Return URL
                  <input type="url" placeholder="https://yoursite.com/done" value={returnUrl} onChange={(e) => setReturnUrl(e.target.value)} />
                </label>
                <label>Sites allowed to embed the page (one per line)
                  <textarea rows={2} placeholder="https://yoursite.com" value={originsText} onChange={(e) => setOriginsText(e.target.value)} />
                </label>
                <p className="studio-hint">Users who finish a session are sent to the return URL. Leave the list empty to allow any site to embed the page.</p>
              </Section>

              <Section id="use" title="Use it" open={openSections.includes("use")} onToggle={toggleSection}>
                <p className="studio-hint">After you save, your backend sends only the avatar and domain. The look is applied automatically.</p>
                <CodeBlock title="Create a session (your backend)" code={backendSnippet} />
                <div className="studio-row">
                  <label>Avatar
                    <select value={avatarId} onChange={(e) => setAvatarId(e.target.value)}>
                      {avatars.map((a) => <option key={a.id} value={a.id}>{a.ethnicity}</option>)}
                    </select>
                  </label>
                  <label>Domain
                    <select value={domain} onChange={(e) => setDomain(e.target.value)}>
                      {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </label>
                </div>
                <button type="button" className="studio-btn studio-btn-primary studio-wide" disabled={busy || !avatarId || dirty} onClick={createTestSession}>
                  Try it with a real session
                </button>
                <p className="studio-hint">
                  {dirty ? "Save your changes first." : testCreated ? "Opened in a new tab. The link works once." : "Opens your saved page in a new tab, the way your users will see it."}
                </p>
              </Section>
            </>
          )}
        </aside>
      </div>

      {showAuth && (
        <AuthPopup onClose={() => setShowAuth(false)} redirectPath={location.pathname} />
      )}
    </div>
  );
};

export default AvatarStudio;
